import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Box } from '@mui/material';
import { spacing, colors } from 'common/theme';
import { getUserData } from 'redux/actions';
import { setTokens } from 'redux/slices/tokenSlice';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { AutocompleteFieldHot } from 'components/common/AutocompleteFieldHot';
import { DatePickerFieldHot } from 'components/common/DatePickerFieldHot';
import { OptionSelectorHot } from 'components/common/OptionSelectorHot';
import { PhoneValidationModal } from 'components/common/PhoneValidationModal';
import { GeneralModal } from 'components/common/GeneralModal';
import Recaptcha, { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import GenderMenIcon from 'assets/personal_zone/Gender_Men.svg';
import GenderWomenIcon from 'assets/personal_zone/Gender_Women.svg';
import { REGEX_PATTERNS, VALIDATION_MESSAGES } from 'app/validation/validation';
import { useGenerateOtpMutation, useEditUserInformationMutation, useRegisterMutation } from 'redux/api/users.api';
import { executeWithCaptcha, generateTestId } from 'app/functions';
import { GENDER, OTP_PROCESS } from 'pages/personal-zone/utils/enums';
import AnalyticsManager from 'app/analytics';
import { ButtonHot } from '../ButtonHot';
import { CheckboxHot } from '../CheckboxHot';
import { padId } from 'pages/personal-zone/utils/functions';
import { useTranslations } from 'common/hooks/useTranslations';
export const PersonalInfoForm = ({
  testID,
  userData,
  disabled = false,
  process = OTP_PROCESS.VERIFY,
  onSuccess,
  onOtpStateChange,
  onCloseModal,
  control: externalControl,
  citiesOptions: externalCitiesOptions,
  workPlacesOptions: externalWorkPlacesOptions,
  showRegistrationConsent = false,
  consentText = {},
  isMobile = false,
  hasUserData = false,
  is_show_marketing_checkbox = false,
  marketing_consent,
  setMarketing_consent,
  neededDataShareAgreement = false,
  data_sharing_consent,
  setData_sharing_consent,
  onDataSharingInfoClick,
  userId,
  ccDigits,
  submitLabel = null,
  renderPhoneValidationExternally = false,
  onOtpRequest,
  extraUserData,
  isEditing = false,
  onRegistrationResponse,
  onCloseAllModals,
}) => {
  const dispatch = useDispatch();
  const citiesArr = useSelector((store) => store.config.citiesArr);
  const workPlaces = useSelector((store) => store.config.WorkPlaces);
  const [isPhoneValidationOpen, setIsPhoneValidationOpen] = useState(false);
  const [registrationPayload, setRegistrationPayload] = useState(null);
  const [otpResponse, setOtpResponse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [isSuccessResponse, setIsSuccessResponse] = useState(false);
  const [generateOtpErrorModalOpen, setGenerateOtpErrorModalOpen] = useState(false);
  const [generateOtpErrorMessage, setGenerateOtpErrorMessage] = useState('');
  const { translate } = useTranslations();

  useEffect(() => {
    if (responseModalOpen && process === OTP_PROCESS.REGISTER && onCloseAllModals && isSuccessResponse) {
      onCloseAllModals();
    }
  }, [responseModalOpen, process, onCloseAllModals, isSuccessResponse]);

  const [generateOtpMutation] = useGenerateOtpMutation();
  const [editUserInformation] = useEditUserInformationMutation();
  const [registerMutation] = useRegisterMutation();

  const isExternalForm = !!externalControl;

  const internalForm = useForm({
    defaultValues: {
      first_name: '', last_name: '', email: '', phone: '',
      workPlaceId: null, cityId: null, gender: 0, birthday: null,
      policy: userData?.policyAccepted || false,
      marketing: extraUserData?.isMarketingApproved || false,
      dataSharing: extraUserData?.dataSharingAgreement || false
    },
  });

  const control = isExternalForm ? externalControl : internalForm.control;
  const { handleSubmit, reset, formState: { isDirty, dirtyFields } } = internalForm;

  useEffect(() => {
    if (!isExternalForm && userData) {
      reset({
        first_name: userData.first_name || '', last_name: userData.last_name || '',
        email: userData.email || '', phone: userData.phone || '',
        workPlaceId: userData.workPlaceId || null, cityId: userData.cityId || null,
        gender: parseInt(userData.gender) || 0,
        birthday: userData.birthday ? new Date(userData.birthday * 1000) : null,
        policy: userData?.policyAccepted || false, marketing: extraUserData?.isMarketingApproved || false, dataSharing: extraUserData?.dataSharingAgreement || false
      });
    }
  }, [userData, reset, isExternalForm, extraUserData]);

  const citiesOptions = useMemo(() => {
    if (externalCitiesOptions && Array.isArray(externalCitiesOptions)) return externalCitiesOptions;
    return citiesArr ? Object.values(citiesArr).map(c => ({ id: c.id, label: c.text })) : [];
  }, [externalCitiesOptions, citiesArr]);

  const workPlacesOptions = useMemo(() => {
    if (externalWorkPlacesOptions && Array.isArray(externalWorkPlacesOptions)) return externalWorkPlacesOptions;
    return workPlaces ? workPlaces.map(wp => ({ id: wp.id, label: wp.text })) : [];
  }, [externalWorkPlacesOptions, workPlaces]);

  const handleFinalSuccess = async (apiResult) => {
    if (!apiResult) {
      const errorMessage = process === 'register' ? 'שגיאה בהרשמה' : 'שגיאה בעדכון הפרטים';
      if (onRegistrationResponse) {
        onRegistrationResponse({ type: 'error', message: errorMessage });
      } else {
        setIsSuccessResponse(false);
        setResponseMessage(errorMessage);
        setResponseModalOpen(true);
      }
      return;
    }

    if (apiResult?.error) {
      const errorMessage = apiResult?.error?.content || (process === 'register' ? 'שגיאה בהרשמה' : 'שגיאה בעדכון הפרטים');
      if (onRegistrationResponse) {
        onRegistrationResponse({ type: 'error', message: errorMessage, result: apiResult?.error?.content });
      } else {
        setIsSuccessResponse(false);
        setResponseMessage(errorMessage);
        setResponseModalOpen(true);
      }
      return;
    }

    const data = apiResult?.data || apiResult;

    if (!data || (typeof data === 'string' && data.trim() === '')) {
      const errorMessage = process === 'register' ? 'שגיאה בהרשמה' : 'שגיאה בעדכון הפרטים';
      if (onRegistrationResponse) {
        onRegistrationResponse({ type: 'error', message: errorMessage });
      } else {
        setIsSuccessResponse(false);
        setResponseMessage(errorMessage);
        setResponseModalOpen(true);
      }
      return;
    }

    dispatch(setTokens({ authToken: data.accessToken || data.token }));
    dispatch(getUserData());

    AnalyticsManager.updateUserProperties(data);
    AnalyticsManager.logRegister(data);

    if (onCloseModal) {
      onCloseModal();
    }

    const successMessage = process === 'register' && !hasUserData ? 'הרשמה הושלמה בהצלחה' : 'הפרטים עודכנו בהצלחה';
    if (onRegistrationResponse) {
      onRegistrationResponse({ type: 'success', message: successMessage, result: data });
    } else {
      setIsSuccessResponse(true);
      setResponseMessage(successMessage);
      setResponseModalOpen(true);
    }

    if (onSuccess) onSuccess(data);
  };

  const handleVerificationSuccess = async (otpResult) => {
    try {
      const sessionToken = otpResult?.sessionToken || otpResult?.authToken;
      dispatch(setTokens({ sessionToken }));

      const finalPayload = { ...registrationPayload, sessionToken };

      if (process === 'register' && !hasUserData) {
        executeWithCaptcha(async (token) => {
          try {
            const result = await registerMutation({ ...finalPayload, 'g-recaptcha-response': token });

            if (result?.error || (result && !result.data && result.error)) {
              const errorMessage = result?.error?.content || 'שגיאה בהרשמה';

              if (onRegistrationResponse) {
                onRegistrationResponse({ type: 'error', message: errorMessage, result: result?.error?.content });
              } else {
                setIsPhoneValidationOpen(false);
                if (onOtpStateChange) onOtpStateChange(false);
                setIsSuccessResponse(false);
                setResponseMessage(errorMessage);
                setResponseModalOpen(true);
              }
              return;
            }

            setIsPhoneValidationOpen(false);
            if (onOtpStateChange) onOtpStateChange(false);
            await handleFinalSuccess(result);
          } catch (error) {
            const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בעדכון הפרטים';

            if (onRegistrationResponse) {
              onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
            } else {
              setIsPhoneValidationOpen(false);
              if (onOtpStateChange) onOtpStateChange(false);
              setIsSuccessResponse(false);
              setResponseMessage(errorMessage);
              setResponseModalOpen(true);
            }
          }
        }, RECAPTCHA_ACTIONS.REGISTER_USER);
      } else {
        executeWithCaptcha(async (token) => {
          try {
            const result = await editUserInformation({ ...finalPayload, 'g-recaptcha-response': token });

            if (result?.error || (result && !result.data && result.error)) {
              const errorMessage = result?.error?.content || 'שגיאה בעדכון הפרטים';

              if (onRegistrationResponse) {
                onRegistrationResponse({ type: 'error', message: errorMessage, result: result?.error?.content });
              } else {
                setIsPhoneValidationOpen(false);
                if (onOtpStateChange) onOtpStateChange(false);
                setIsSuccessResponse(false);
                setResponseMessage(errorMessage);
                setResponseModalOpen(true);
              }
              return;
            }

            AnalyticsManager.logEditUserInformation();
            setIsPhoneValidationOpen(false);
            if (onOtpStateChange) onOtpStateChange(false);
            await handleFinalSuccess(result);
          } catch (error) {
            const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בעדכון הפרטים';

            if (onRegistrationResponse) {
              onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
            } else {
              setIsPhoneValidationOpen(false);
              if (onOtpStateChange) onOtpStateChange(false);
              setIsSuccessResponse(false);
              setResponseMessage(errorMessage);
              setResponseModalOpen(true);
            }
          }
        }, RECAPTCHA_ACTIONS.EDIT_USER_INFORMATION);
      }
    } catch (error) {
      setIsPhoneValidationOpen(false);
      if (onOtpStateChange) onOtpStateChange(false);
      const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בעדכון הפרטים';
      if (onRegistrationResponse) {
        onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
      } else {
        setIsSuccessResponse(false);
        setResponseMessage(errorMessage);
        setResponseModalOpen(true);
      }
    }
  };

  const onSubmit = (data) => {
    const payload = {
      ...data,
      birthDate: data.birthday ? Math.floor(new Date(data.birthday).getTime() / 1000) : 0,
      userId: userId || userData?.user_id,
      policy_check: data.policy,
      data_sharing_consent: data.dataSharing ?? false,
      marketing_consent: data.marketing ?? false,
      is_first_login: process === OTP_PROCESS.REGISTER ? 'true' : undefined,
    };

    setRegistrationPayload(payload);
    setIsSubmitting(true);

    
    // Generate OTP with captcha and ccDigits
    executeWithCaptcha(async (token) => {
      try {
        const result = await generateOtpMutation({
          userId: padId(payload.userId),
          cellphone: String(data.phone),
          ccDigits,
          email: data.email != null ? String(data.email) : data.email,
          process,
          'g-recaptcha-response': token
        });

        if (result.error){
          const errorMessage = result?.error?.content || 'שגיאה בשליחת קוד אימות';  
          setGenerateOtpErrorMessage(errorMessage);
          setGenerateOtpErrorModalOpen(true);
          setResponseModalOpen(true);
          return;
        }

        setOtpResponse(result.data);
        if (onOtpStateChange) onOtpStateChange(true);

        if (renderPhoneValidationExternally && onOtpRequest) {
          const internalHandleSuccess = async (otpResult) => {
            try {
              const sessionToken = otpResult?.sessionToken || otpResult?.authToken;
              dispatch(setTokens({ sessionToken }));

              const finalPayload = { ...payload, sessionToken };

              if (process === 'register' && !hasUserData) {
                executeWithCaptcha(async (token) => {
                  try {
                    const result = await registerMutation({ ...finalPayload, 'g-recaptcha-response': token });

                    if (result?.error || (result && !result.data && result.error)) {
                      const errorMessage = result?.error?.content || 'שגיאה בהרשמה';
                      if (onRegistrationResponse) {
                        onRegistrationResponse({ type: 'error', message: errorMessage, result: result?.error?.content });
                      } else {
                        if (onOtpStateChange) onOtpStateChange(false);
                        setIsSuccessResponse(false);
                        setResponseMessage(errorMessage);
                        setResponseModalOpen(true);
                      }
                      return;
                    }

                    if (onOtpStateChange) onOtpStateChange(false);
                    await handleFinalSuccess(result);
                  } catch (error) {
                    const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בשליחת קוד אימות';
                    if (onRegistrationResponse) {
                      onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
                    } else {
                      if (onOtpStateChange) onOtpStateChange(false);
                      setIsSuccessResponse(false);
                      setResponseMessage(errorMessage);
                      setResponseModalOpen(true);
                    }
                  }
                }, RECAPTCHA_ACTIONS.REGISTER_USER);
              } else {
                executeWithCaptcha(async (token) => {
                  try {
                    const result = await editUserInformation({ ...finalPayload, 'g-recaptcha-response': token });

                    if (result?.error || (result && !result.data && result.error)) {
                      const errorMessage = result?.error?.content || 'שגיאה בעדכון הפרטים';

                      if (onRegistrationResponse) {
                        onRegistrationResponse({ type: 'error', message: errorMessage, result: result?.error?.content });
                      } else {
                        if (onOtpStateChange) onOtpStateChange(false);
                        setIsSuccessResponse(false);
                        setResponseMessage(errorMessage);
                        setResponseModalOpen(true);
                      }
                      return;
                    }

                    AnalyticsManager.logEditUserInformation();
                    if (onOtpStateChange) onOtpStateChange(false);
                    await handleFinalSuccess(result);
                  } catch (error) {
                    const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בעדכון הפרטים';

                    if (onRegistrationResponse) {
                      onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
                    } else {
                      if (onOtpStateChange) onOtpStateChange(false);
                      setIsSuccessResponse(false);
                      setResponseMessage(errorMessage);
                      setResponseModalOpen(true);
                    }
                  }
                }, RECAPTCHA_ACTIONS.EDIT_USER_INFORMATION);
              }
            } catch (error) {
              const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בעדכון הפרטים';
              if (onRegistrationResponse) {
                onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
              } else {
                if (onOtpStateChange) onOtpStateChange(false);
                setIsSuccessResponse(false);
                setResponseMessage(errorMessage);
                setResponseModalOpen(true);
              }
            }
          };

          onOtpRequest({
            phone: data.phone,
            userId: payload.userId,
            email: data.email,
            otpResponse: result,
            registrationPayload: payload,
            onVerificationSuccess: internalHandleSuccess,
          });
        } else {
          setIsPhoneValidationOpen(true);
        }
      } catch (error) {
        const errorMessage = error?.data?.error?.content || error?.data || 'שגיאה בשליחת קוד אימות';
        if (onRegistrationResponse) {
          onRegistrationResponse({ type: 'error', message: errorMessage, result: error?.data?.error?.content });
        } else {
          setFormError(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    }, RECAPTCHA_ACTIONS.VERIFY_CODE);
  };

  return (
    <Box component="form" onSubmit={!isExternalForm ? handleSubmit(onSubmit) : undefined} testID={testID}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, rowGap: { xs: `${spacing.xs}px`, md: `${spacing.md}px` }, columnGap: { xs: 0, md: '60px', lg: '100px' } }} testID={generateTestId(testID, 'form-grid')}>
        <Controller name="first_name" control={control} rules={{ required: VALIDATION_MESSAGES.REQUIRED, pattern: { value: REGEX_PATTERNS.FIRST_NAME, message: VALIDATION_MESSAGES.FIRST_NAME } }} render={({ field, fieldState }) => (
          <TextInputHot label="שם פרטי" mode={MODE.FORM} placeholder="הזינו שם פרטי" {...field} disabled={disabled} error={!!fieldState.error} helperText={fieldState.error?.message} testID={generateTestId(testID, 'first-name-input')} />
        )} />
        <Controller name="last_name" control={control} rules={{ required: VALIDATION_MESSAGES.REQUIRED, pattern: { value: REGEX_PATTERNS.LAST_NAME, message: VALIDATION_MESSAGES.LAST_NAME } }} render={({ field, fieldState }) => (
          <TextInputHot label="שם משפחה" mode={MODE.FORM} placeholder="הזינו שם משפחה" {...field} disabled={disabled} error={!!fieldState.error} helperText={fieldState.error?.message} testID={generateTestId(testID, 'last-name-input')} />
        )} />
        <Controller name="email" control={control} rules={{ required: VALIDATION_MESSAGES.REQUIRED, pattern: { value: REGEX_PATTERNS.EMAIL, message: VALIDATION_MESSAGES.EMAIL } }} render={({ field, fieldState }) => (
          <TextInputHot label="מייל" mode={MODE.FORM} placeholder="הזינו מייל" {...field} disabled={disabled} error={!!fieldState.error} helperText={fieldState.error?.message} testID={generateTestId(testID, 'email-input')} />
        )} />
        <Controller name="phone" control={control} rules={{ required: VALIDATION_MESSAGES.REQUIRED, pattern: { value: REGEX_PATTERNS.PHONE, message: VALIDATION_MESSAGES.PHONE } }} render={({ field, fieldState }) => (
          <TextInputHot label="טלפון" mode={MODE.FORM} placeholder="הזינו טלפון" {...field} disabled={disabled} error={!!fieldState.error} helperText={fieldState.error?.message} testID={generateTestId(testID, 'phone-input')} />
        )} />
        <Controller name="workPlaceId" control={control} render={({ field, fieldState }) => (
          <AutocompleteFieldHot label="חברה" options={workPlacesOptions} value={workPlacesOptions.find(o => Number(o.id) === Number(field.value)) || null} onChange={(_, v) => field.onChange(v ? v.id : null)} disabled={disabled} error={!!fieldState.error} placeholder="בחרו חברה" helperText={fieldState.error?.message} testID={generateTestId(testID, 'workplace-input')} />
        )} />
        <Controller name="cityId" control={control} render={({ field, fieldState }) => (
          <AutocompleteFieldHot label="עיר" options={citiesOptions} value={citiesOptions.find(o => Number(o.id) === Number(field.value)) || null} onChange={(_, v) => field.onChange(v ? v.id : null)} disabled={disabled} error={!!fieldState.error} placeholder="בחרו עיר" helperText={fieldState.error?.message} testID={generateTestId(testID, 'city-input')} />
        )} />
        <Controller
          name="birthday"
          control={control}
          rules={{
            required: VALIDATION_MESSAGES.REQUIRED,
            validate: (value) => {
              if (!value) return VALIDATION_MESSAGES.REQUIRED;

              const today = new Date();
              const birthDate = new Date(value);

              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();

              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }

              return age >= 14 || VALIDATION_MESSAGES.AGE_RESTRICTION;
            }
          }}
          render={({ field, fieldState }) => (
            <DatePickerFieldHot
              label="תאריך לידה"
              {...field}
              disabled={disabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              testID={generateTestId(testID, 'birthday-input')}
            />
          )}
        />
        <Controller name="gender" control={control} rules={{ validate: (v) => [GENDER.FEMALE, GENDER.MALE].includes(Number(v)) || VALIDATION_MESSAGES.REQUIRED }} render={({ field, fieldState }) => (
          <OptionSelectorHot label="מגדר" value={field.value} onChange={field.onChange} disabled={disabled} options={[{ value: GENDER.MALE, icon: GenderMenIcon }, { value: GENDER.FEMALE, icon: GenderWomenIcon }]} error={!!fieldState.error} helperText={fieldState.error?.message} testID={generateTestId(testID, 'gender-input')} />
        )} />
      </Box>

      {process === OTP_PROCESS.REGISTER && showRegistrationConsent && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 1 }, mt: { xs: 4, md: 2 } }} testID={generateTestId(testID, 'consent-container')}>
          {!userData?.policyAccepted && <CheckboxHot name="policy" control={control} required={true} html={consentText?.register_details_screen_terms_checkbox_html_web} linksNotClickedErrorText={consentText?.personal_details_policy_err} testID={generateTestId(testID, 'policy-checkbox')} />}
          {is_show_marketing_checkbox && !extraUserData?.isMarketingApproved && <CheckboxHot name="marketing" control={control} html={consentText?.marketing_consent_text} testID={generateTestId(testID, 'marketing-checkbox')} />}
          {neededDataShareAgreement && !extraUserData?.dataSharingAgreement && <CheckboxHot name="dataSharing" control={control} html={consentText?.agreement_content_register} onAllLinksClicked={onDataSharingInfoClick} testID={generateTestId(testID, 'data-sharing-checkbox')} />}
        </Box>
      )}

      {!disabled && (
        <Box sx={{ mt: { xs: 4, md: 2 }, display: 'flex', justifyContent: { xs: 'center', sm: 'center', md: showRegistrationConsent ? 'center' : 'flex-start' } }} testID={generateTestId(testID, 'submit-container')}>
          <ButtonHot
            type="submit"
            size='l'
            sx={{
              opacity: isDirty ? 1 : 0.6,
              disabled: isDirty ? false : true,
              pointerEvents: isDirty ? 'auto' : 'none',
              cursor: isDirty ? 'pointer' : 'not-allowed',
              ...((!isDirty && isEditing) && {
                backgroundColor: colors?.palette?.gray400,
              }),
            }}
            mode="contained"
            disabled={isSubmitting}
            testID={generateTestId(testID, 'submit-button')}
          >
            {translate('terms_popup_ok_btn')}
          </ButtonHot>
        </Box>
      )}

      {!renderPhoneValidationExternally && (
        <PhoneValidationModal
          open={isPhoneValidationOpen}
          onClose={() => setIsPhoneValidationOpen(false)}
          phone={registrationPayload?.phone}
          userId={registrationPayload?.userId}
          email={registrationPayload?.email || userData?.email}
          initialOtpResponse={otpResponse}
          onVerificationSuccess={handleVerificationSuccess}
          process={process}
          testID={generateTestId(testID, 'phone-validation-modal')}
        />
      )}

      {generateOtpErrorModalOpen && <GeneralModal
        open={generateOtpErrorModalOpen}
        onClose={() => setGenerateOtpErrorModalOpen(false)}
        buttonText="אישור"
        testID={generateTestId(testID, 'generate-otp-error-modal')}
      >
        {generateOtpErrorMessage}
      </GeneralModal>}

      {!onRegistrationResponse && onRegistrationResponse != null && (
        <GeneralModal
          open={responseModalOpen}
          onClose={() => {
            setResponseModalOpen(false);
            if (process === OTP_PROCESS.REGISTER && onCloseAllModals && isSuccessResponse) {
              onCloseAllModals(true);
            } else if (process === OTP_PROCESS.REGISTER && onCloseAllModals && !isSuccessResponse) {
            } else if (onCloseModal) {
              onCloseModal();
            }
          }}
          buttonText="אישור"
          testID={generateTestId(testID, 'response-modal')}
        >
          {responseMessage}
        </GeneralModal>
      )}
      <Box testID={generateTestId(testID, 'recaptcha-container')}>
        <Recaptcha isFunction={true} enterprise={true} onReady={() => { }} />
      </Box>
    </Box>
  );
};

export default PersonalInfoForm;